import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faCheck, faGavel } from '@fortawesome/free-solid-svg-icons';
import GLOBALS from '../config/globals';
import { formatConstraintLabel } from '../helpers/constraintHelpers';
import BackButton from './BackButton';
import DifficultyBadge from './DifficultyBadge';

const getContestState = (contest, userCampaignContests) => {
    const userContest = userCampaignContests?.find(
        ucc => String(ucc.campaignContestID) === String(contest.campaignContestID)
    );
    if (!userContest) return 'not-started';

    const hasWin = userContest.UserCampaignContests?.some(
        ucc => ucc.result === GLOBALS.RESULT.WIN
    );
    if (hasWin) return 'completed';

    const hasActive = userContest.UserCampaignContests?.some(ucc => !ucc.result);
    if (hasActive) return 'in-progress';

    return 'not-started';
};

const getNodeState = (contest, contestState) => {
    if (contest.isLocked && contestState === 'not-started') return 'locked';
    if (contestState === 'completed') return 'completed';
    return 'unlocked';
};

const parseContestName = name => {
    if (!name) return { year: '', team: name || '' };
    const match = name.match(/^(\d{4})\s+(.+)$/);
    return match ? { year: match[1], team: match[2] } : { year: '', team: name };
};

const getStadiumSrc = (contest) => {
    const stadium = contest.stadiumImage || 'tutorial';
    return `/img/stadiums/${stadium}.png`;
};

const getConnectorState = (prevState, nextState) => {
    if (prevState === 'completed' && nextState === 'completed') return 'completed';
    return 'locked';
};

const seededRandom = (seed) => {
    let s = seed;
    return () => {
        s = (s * 16807 + 0) % 2147483647;
        return (s - 1) / 2147483646;
    };
};

const buildWanderingPath = (fromX, fromY, toX, toY, sameRow, segIndex, containerWidth) => {
    const rng = seededRandom(segIndex * 7919 + Math.round(fromX * 31 + fromY * 17));

    if (sameRow) {
        const t = 0.25 + rng() * 0.5;
        const midX = fromX + (toX - fromX) * t;
        const sag = 15 + rng() * 60;
        const dir = rng() > 0.5 ? 1 : -1;
        return `M ${fromX},${fromY} Q ${midX},${fromY + sag * dir} ${toX},${toY}`;
    }

    const t = 0.25 + rng() * 0.5;
    const midY = fromY + (toY - fromY) * t;
    const isRightSide = fromX > containerWidth / 2;
    const outDir = isRightSide ? 1 : -1;
    const spread = 20 + rng() * 100;
    const cpX = (fromX + toX) / 2 + outDir * spread;

    return `M ${fromX},${fromY} Q ${cpX},${midY} ${toX},${toY}`;
};

const ContestNode = ({ contest, nodeState, linkTo, onClick }) => {
    const { year, team } = parseContestName(contest.name);
    const games = contest.numOfGames || 7;
    const stadiumSrc = getStadiumSrc(contest);
    const hasConstraints = contest.constraints?.length > 0;

    return (
        <Link
            to={linkTo}
            className={`adventure-map-node adventure-map-node--${nodeState}`}
            onClick={onClick}
        >
            <div className="adventure-map-node-card">
                {nodeState === 'locked' && (
                    <span className="adventure-map-node-badge adventure-map-node-badge--lock">
                        <FontAwesomeIcon icon={faLock} />
                    </span>
                )}
                {nodeState === 'completed' && (
                    <span className="adventure-map-node-badge adventure-map-node-badge--check">
                        <FontAwesomeIcon icon={faCheck} />
                    </span>
                )}
                {hasConstraints && (
                    <span className="adventure-map-node-constraint">
                        <FontAwesomeIcon icon={faGavel} />
                        <span className="adventure-map-node-constraint-tooltip">
                            {contest.constraints.map((c, i) => (
                                <span key={i} className="adventure-map-node-constraint-line">
                                    {formatConstraintLabel(c)}
                                </span>
                            ))}
                        </span>
                    </span>
                )}
                <img
                    src={stadiumSrc}
                    alt={`${team} stadium`}
                    className="adventure-map-node-stadium"
                    onError={e => { e.target.src = '/img/transparent.png'; }}
                />
                <span className="adventure-map-node-team">
                    {year && <span className="adventure-map-node-year">{year}</span>}
                    {team}
                </span>
                <span className="adventure-map-node-games">
                    {games === 1 ? '1 Game' : `Best of ${games}`}
                </span>
                <DifficultyBadge difficulty={contest.difficulty} />
            </div>
        </Link>
    );
};

ContestNode.propTypes = {
    contest: PropTypes.object.isRequired,
    nodeState: PropTypes.string.isRequired,
    linkTo: PropTypes.string.isRequired,
    onClick: PropTypes.func,
};

const useColumnsFromWidth = (containerRef) => {
    const [columns, setColumns] = useState(4);

    const updateColumns = useCallback(() => {
        if (!containerRef.current) return;
        const width = containerRef.current.offsetWidth;
        if (width < 480) setColumns(2);
        else if (width < 720) setColumns(3);
        else setColumns(4);
    }, [containerRef]);

    useEffect(() => {
        updateColumns();
        const observer = new ResizeObserver(updateColumns);
        if (containerRef.current) observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [containerRef, updateColumns]);

    return columns;
};

const chunkIntoRows = (items, columns) => {
    const rows = [];
    for (let i = 0; i < items.length; i += columns) {
        rows.push(items.slice(i, i + columns));
    }
    return rows;
};

const AddContestNode = ({ onClick }) => (
    <div className="adventure-map-node adventure-map-node--add" onClick={onClick} role="button" tabIndex={0}>
        <div className="adventure-map-node-card adventure-map-node-card--add">
            <span className="adventure-map-node-add-icon">+</span>
            <span className="adventure-map-node-team">Add Contest</span>
        </div>
    </div>
);

const AdventureMap = ({ contests, userCampaignContests, currentUser, onStartContest, isLevelLocked, campaignName, campaignDesc, backTo, isAdmin, onEdit, onAddContest, editMode }) => {
    const containerRef = useRef(null);
    const nodeRefs = useRef({});
    const columns = useColumnsFromWidth(containerRef);
    const [pathSegments, setPathSegments] = useState([]);

    const contestsWithState = useMemo(() =>
        contests.map(contest => {
            const contestState = currentUser
                ? getContestState(contest, userCampaignContests)
                : 'not-started';
            const nodeState = getNodeState(contest, contestState);

            const userContest = userCampaignContests?.find(
                ucc => String(ucc.campaignContestID) === String(contest.campaignContestID)
            );
            const activeAttempt = userContest?.UserCampaignContests?.find(ucc => !ucc.result);

            let linkTo = `/campaign-contests/${contest.campaignContestID}`;
            if (activeAttempt) {
                linkTo = `/campaign-contests/${activeAttempt.userCampaignContestID}/play`;
            }

            return { contest, nodeState, contestState, linkTo };
        }),
    [contests, userCampaignContests, currentUser, editMode]);

    const rows = chunkIntoRows(contestsWithState, columns);

    const statesRef = useRef(contestsWithState);
    statesRef.current = contestsWithState;

    const calculatePaths = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;

        const items = statesRef.current;
        const containerRect = container.getBoundingClientRect();
        const segments = [];

        for (let i = 0; i < items.length - 1; i++) {
            const fromEl = nodeRefs.current[i];
            const toEl = nodeRefs.current[i + 1];
            if (!fromEl || !toEl) continue;

            const fromRect = fromEl.getBoundingClientRect();
            const toRect = toEl.getBoundingClientRect();

            const sameRow = Math.abs(fromRect.top - toRect.top) < fromRect.height / 2;

            let fromX, fromY, toX, toY;
            if (sameRow) {
                const goingRight = fromRect.left < toRect.left;
                if (goingRight) {
                    fromX = fromRect.right - containerRect.left;
                    toX = toRect.left - containerRect.left;
                } else {
                    fromX = fromRect.left - containerRect.left;
                    toX = toRect.right - containerRect.left;
                }
                fromY = fromRect.top + fromRect.height / 2 - containerRect.top;
                toY = toRect.top + toRect.height / 2 - containerRect.top;
            } else {
                fromX = fromRect.left + fromRect.width / 2 - containerRect.left;
                fromY = fromRect.bottom - containerRect.top;
                toX = toRect.left + toRect.width / 2 - containerRect.left;
                toY = toRect.top - containerRect.top;
            }

            const state = getConnectorState(items[i].nodeState, items[i + 1].nodeState);
            const d = buildWanderingPath(fromX, fromY, toX, toY, sameRow, i, containerRect.width);

            segments.push({ d, state });
        }

        setPathSegments(segments);
    }, [columns, contestsWithState]);

    useLayoutEffect(() => {
        calculatePaths();
    }, [calculatePaths]);

    useEffect(() => {
        const observer = new ResizeObserver(() => requestAnimationFrame(calculatePaths));
        if (containerRef.current) observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [calculatePaths]);

    return (
        <div className="adventure-map" ref={containerRef}>
            <div className="adventure-map-header">
                {backTo && <BackButton to={backTo} />}
                <h2 className="adventure-map-title">{campaignName}</h2>
                {campaignDesc && <p className="adventure-map-desc">{campaignDesc}</p>}
                {isAdmin && onEdit && <button className="adventure-map-edit-btn" onClick={onEdit}>Edit</button>}
            </div>
            <svg className="adventure-map-svg">
                {pathSegments.map((seg, i) => (
                    <path
                        key={i}
                        d={seg.d}
                        className={`adventure-map-svg-path adventure-map-svg-path--${seg.state}`}
                    />
                ))}
            </svg>
            {rows.map((row, rowIndex) => {
                const isReversed = rowIndex % 2 === 1;
                const displayRow = isReversed ? [...row].reverse() : row;

                return (
                    <div key={rowIndex} className="adventure-map-row">
                        {displayRow.map((item, colIndex) => {
                            const globalIndex = rowIndex * columns +
                                (isReversed ? (row.length - 1 - colIndex) : colIndex);

                            const canStart = item.nodeState === 'unlocked'
                                && item.contestState === 'not-started'
                                && currentUser && !isLevelLocked;

                            const onClick = !editMode && canStart
                                ? e => { e.preventDefault(); onStartContest(item.contest.campaignContestID); }
                                : undefined;

                            return (
                                <div
                                    key={item.contest.campaignContestID}
                                    className="adventure-map-cell"
                                    ref={el => { nodeRefs.current[globalIndex] = el; }}
                                >
                                    <ContestNode
                                        contest={item.contest}
                                        nodeState={item.nodeState}
                                        linkTo={item.linkTo}
                                        onClick={onClick}
                                    />
                                    {isAdmin && (
                                        <Link to={`/campaign-contests/${item.contest.campaignContestID}/edit`} className="adventure-map-node-edit-btn">
                                            Edit
                                        </Link>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
            {onAddContest && (
                <div className="adventure-map-row">
                    <div className="adventure-map-cell">
                        <AddContestNode onClick={onAddContest} />
                    </div>
                </div>
            )}
        </div>
    );
};

AdventureMap.propTypes = {
    contests: PropTypes.array.isRequired,
    userCampaignContests: PropTypes.array,
    currentUser: PropTypes.object,
    onStartContest: PropTypes.func.isRequired,
    isLevelLocked: PropTypes.bool,
    campaignName: PropTypes.string,
    campaignDesc: PropTypes.string,
    backTo: PropTypes.string,
    isAdmin: PropTypes.bool,
    onEdit: PropTypes.func,
    onAddContest: PropTypes.func,
    editMode: PropTypes.bool,
};

export default AdventureMap;
